import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import { MuiContainer, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import RichText, { CaptureClicksRichText } from '@/features/shared/RichText';
import { Theme } from '@/utils/constants';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { ChangeEvent, forwardRef, memo, useState } from 'react';
import { VerticalContentProps } from './VerticalContent.interface';
import useStyles from './VerticalContent.styles';

const a11yProps = (index: any, isMobile: boolean) => {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': [
      `vertical-tabpanel-${index}`,
      isMobile ? `vertical-mobiletabpanel-${index}` : '',
    ]
      .join(' ')
      .trim(),
  };
};

const mapVariantToProps = ({ variant }: any) => {
  if (/^image$/.test(variant)) {
    return {
      type: 'featured',
    };
  }

  return {};
};

const mapVariantToDetailContentClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    if (/^link$/.test(variant)) {
      return classes.link;
    }

    return '';
  };

interface TabPanelProps {
  children?: any;
  index?: any;
  value?: any;
  panelName?: string;
  classes?: any;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, panelName, classes, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-${panelName}-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={classes.tabPanel}
      {...other}
    >
      {children}
    </div>
  );
};

const TabDescription = ({ theme, description, classes }: any) => {
  if (!description) return null;

  return (
    <CaptureClicksRichText
      customStyles
      content={description}
      theme={theme}
      className={classes.tabDescription}
    />
  );
};

const DetailContentPanels = ({
  classes,
  tabs = [],
  value = 0,
  theme,
}: {
  classes: any;
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
}) => {
  return (
    <>
      {tabs.map(({ tabDetailContent }, i) => {
        const { mediaContent, detailContent, heading, superHeading } = tabDetailContent || {};

        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={`tabPanel_${i}`}
            value={value}
            panelName="tabpanel"
            index={i}
            classes={classes}
          >
            <div className={classes.media}>
              <DetailContent
                content={mediaContent}
                theme={theme}
                mapVariantToProps={mapVariantToProps}
              />
            </div>
            {superHeading && (
              <MuiTypography variant="overline" component="h5" className={classes.superheading}>
                {superHeading}
              </MuiTypography>
            )}
            {heading && (
              <RichText
                customStyles
                theme={theme}
                className={`${classes.heading} gtm-headline`}
                content={heading}
              />
            )}
            <DetailContent
              content={detailContent}
              mapVariantToClassName={mapVariantToDetailContentClassName(classes)}
              theme={theme}
            />
          </TabPanel>
        );
      })}
    </>
  );
};

const MobileHeadingPanels = ({
  tabs,
  value,
  theme,
  classes,
}: {
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
  classes: any;
}) => (
  <>
    {tabs.map(({ tabDescription }, i) => (
      <TabPanel
        // eslint-disable-next-line react/no-array-index-key
        key={`tabPanel_${i}`}
        value={value}
        panelName="mobiletabpanel"
        index={i}
        classes={classes}
      >
        <TabDescription description={tabDescription} theme={theme} classes={classes} />
      </TabPanel>
    ))}
  </>
);

const CustomTabComponent = forwardRef((props: any, ref: any) => {
  const { children, tabDescription, ...tabProps } = props;
  return (
    <div {...tabProps} ref={ref}>
      <MuiTypography className="heading gtm-headline" variant="body1" component="h3">
        {children}
      </MuiTypography>
      {tabDescription}
    </div>
  );
});

const VerticalContent = ({
  theme = Theme.light,
  tabGroupTitle,
  tabs = [],
  containerProps,
  contentButtons,
}: Br.PropsWithContentButtons<VerticalContentProps>) => {
  const classes = useStyles({});
  const [value, setValue] = useState(0);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const rootClasses = clsx(classes.root, `${theme}-theme`, 'large');

  return (
    <ContentContainer
      {...containerProps}
      mobileTopBackgroundColor={containerProps?.topBackgroundColor}
      mobileBottomBackgroundColor={containerProps?.bottomBackgroundColor}
      topSpacing={containerProps?.topSpacing}
      bottomSpacing={containerProps?.bottomSpacing}
      data-component-variant="vertical-content"
    >
      <MuiContainer className={rootClasses}>
        <div className={classes.content}>
          <div className="tabPanels">
            <DetailContentPanels tabs={tabs} value={value} classes={classes} theme={theme} />
          </div>
          <div className="tabs">
            {contentButtons?.content}

            {tabGroupTitle && (
              <MuiTypography variant="overline" component="h3" className={classes.title}>
                {tabGroupTitle}
              </MuiTypography>
            )}

            {tabs.length > 0 && (
              <MuiTabs
                orientation={isMobile ? 'horizontal' : 'vertical'}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons={isMobile ? 'on' : 'off'}
                value={value}
                onChange={handleChange}
                aria-label="Vertical tabs"
                className={classes.tabs}
              >
                {tabs.map(({ tabTitle, tabDescription }, i) => (
                  <MuiTab
                    // eslint-disable-next-line react/no-array-index-key
                    key={`tab_${i}`}
                    label={tabTitle?.text}
                    {...a11yProps(i, isMobile)}
                    component={CustomTabComponent}
                    className={clsx(classes.tab, 'gtm-tab')}
                    tabDescription={
                      !isMobile && (
                        <TabDescription
                          description={tabDescription}
                          theme={theme}
                          classes={classes}
                        />
                      )
                    }
                  />
                ))}
              </MuiTabs>
            )}

            {isMobile && (
              <MobileHeadingPanels theme={theme} tabs={tabs} value={value} classes={classes} />
            )}
          </div>
        </div>
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(VerticalContent);
